import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';

/**
 * Hook genérico pra preferências de UI por usuário — substitui o uso de
 * `useLocalStorage` (deletado no commit 7) pra qualquer key de UI prefs
 * (filtros do comercial, view mode, sort state, etc).
 *
 * **Backend-first (2026-05-17):** persiste em `user_ui_preferences` via
 * `/api/v1/users/me/ui-preferences/:key`. Sem `localStorage` pra dados de
 * negócio (regra inviolável).
 *
 * API similar a useState — `[value, setValue, remove]`. Mas:
 *   - Boot: GET retorna valor salvo OU defaultValue (404 silencioso)
 *   - setValue: optimistic — UI atualiza imediato + PUT em background
 *   - Falha de PUT NÃO reverte UI (usuário toggleia de novo se quiser)
 *
 * Exemplo:
 *   const [view, setView] = useUiPreference<'kanban' | 'lista'>(
 *     'comercial.view', 'kanban'
 *   );
 */
export function useUiPreference<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const qc = useQueryClient();

  const [optimistic, setOptimistic] = useState<T | null>(null);

  const { data: backendValue } = useQuery({
    queryKey: ['v1', 'ui-preferences', key],
    queryFn: async () => {
      try {
        const r = await api.get<{ key: string; value: T } | unknown[]>(
          `/api/v1/users/me/ui-preferences/${key}`
        );
        // Backend retorna {key, value} no GET — mas em ambientes de teste
        // onde o mock devolve outra shape (ex: []), tratar como "não tem
        // preferência" e cair no default. Evita warning "Query data cannot
        // be undefined" do React Query.
        if (r && typeof r === 'object' && 'value' in r && r.value !== undefined) {
          return (r as { value: T }).value;
        }
        return defaultValue;
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 404) return defaultValue;
        throw err;
      }
    },
    enabled: hasToken(),
    staleTime: 60_000,
  });

  const current = optimistic ?? backendValue ?? defaultValue;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(current) : next;
      setOptimistic(resolved);
      void (async () => {
        try {
          await api.put(`/api/v1/users/me/ui-preferences/${key}`, { value: resolved });
          await qc.invalidateQueries({ queryKey: ['v1', 'ui-preferences', key] });
        } catch {
          // mantém UI no estado novo (sem reverter) — UX-first
        }
      })();
    },
    [key, qc, current]
  );

  const remove = useCallback(() => {
    setOptimistic(defaultValue);
    void (async () => {
      try {
        await api.delete(`/api/v1/users/me/ui-preferences/${key}`);
        await qc.invalidateQueries({ queryKey: ['v1', 'ui-preferences', key] });
      } catch {
        // tolera 4xx
      }
    })();
  }, [key, defaultValue, qc]);

  return [current, setValue, remove];
}
